import { useCallback, useState } from "react";
import { useClassrooms } from "../data/ClassroomContext";
import { isTeacherWorkAvailable } from "../data/teacherWorkApi";
import useLearnerActivity from "../data/useLearnerActivity";
import ActivityPreviewDialog from "./ActivityPreviewDialog";
import AssignDialog from "./AssignDialog";
import ui from "./ui.module.css";

// One lesson step's approved learner activities, each with Preview and Assign.
const BlockActivities = ({ lessonId, blockId, onPreview, onAssign }) => {
  const found = useLearnerActivity(lessonId, blockId);
  if (found.status === "checking") return null;
  if (found.status === "problem") {
    return (
      <li className={ui.meta}>
        An activity could not be checked.{" "}
        <button type="button" className={ui.textButton} onClick={found.retry}>
          Check again
        </button>
      </li>
    );
  }
  return found.activities.map((activity) => (
    <li key={activity.id} className={`${ui.card} ${ui.workItem}`}>
      <p className={ui.cardTitle}>{activity.title || "Learner activity"}</p>
      <div className={ui.sheetActions}>
        <button type="button" className={`${ui.button} ${ui.secondary}`} onClick={() => onPreview(activity)}>
          Preview
        </button>
        <button type="button" className={`${ui.button} ${ui.primary}`} onClick={() => onAssign(activity)}>
          Assign
        </button>
      </div>
    </li>
  ));
};

// Work for learners to do outside the lesson: homework, practice or a test
// (Handoff 007 §9). Kept closed until wanted, so the lesson's one primary
// action stays "Start lesson". Activities are looked up only when opened.
const SetWork = ({ lessonId, blocks }) => {
  const { active } = useClassrooms();
  const [open, setOpen] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const closePreview = useCallback(() => setPreviewing(null), []);
  const closeAssign = useCallback(() => setAssigning(null), []);

  if (!isTeacherWorkAvailable() || !blocks?.length) return null;

  return (
    <details className={ui.details} onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary>Set work for later</summary>
      {open && (
        <div className={ui.stack}>
          <p className={ui.meta}>
            Send an activity from this lesson as homework, practice or a test. Learners find it on their tablets.
          </p>
          <ul className={ui.list}>
            {blocks.map((block) => (
              <BlockActivities
                key={block.id}
                lessonId={lessonId}
                blockId={block.id}
                onPreview={setPreviewing}
                onAssign={setAssigning}
              />
            ))}
          </ul>
        </div>
      )}
      {previewing && (
        <ActivityPreviewDialog classroomId={active.id} activityId={previewing.id} onClose={closePreview} />
      )}
      {assigning && (
        <AssignDialog classroomId={active.id} className={active.name} activity={assigning} onClose={closeAssign} />
      )}
    </details>
  );
};

export default SetWork;
